/**
 * BragBoardPanel — Phase 20.13
 * ------------------------------------------------------------------
 * Renders the league's Brag Board collages (weekly Pillow-stitched
 * proof grids) with a native [ SHARE STATUS ] button per card.
 *
 * Share strategy:
 *   1. Try `navigator.share({ files: [File] })` — opens the OS share
 *      sheet so users hop straight into WhatsApp / Messages / Stories.
 *   2. If the browser supports share() but not file-share, fall back to
 *      `navigator.share({ url })` so the recipient gets a clickable
 *      composite URL.
 *   3. If `navigator.share` is unavailable (desktop Chrome/Firefox),
 *      trigger a normal download via an `<a download>` blob link.
 *
 * Brand aesthetic mirrors the Ledger / Hall of Fame:
 *   • border + var(--stakes-border)
 *   • font-anton header + label-mono badges
 *   • cyan accent for the active week + cyan share button
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function formatWeekLabel(weekStart, weekEnd) {
  if (!weekStart) return "—";
  // weekStart and weekEnd are ISO YYYY-MM-DD strings
  try {
    const s = new Date(`${weekStart}T00:00:00`);
    const e = weekEnd ? new Date(`${weekEnd}T00:00:00`) : null;
    const opts = { month: "short", day: "2-digit" };
    const startTxt = s.toLocaleDateString(undefined, opts).toUpperCase();
    const endTxt = e ? e.toLocaleDateString(undefined, opts).toUpperCase() : "";
    return endTxt ? `${startTxt} → ${endTxt}` : startTxt;
  } catch {
    return weekStart;
  }
}

async function fetchAsFile(url, filename) {
  const resp = await fetch(url, { credentials: "omit" });
  if (!resp.ok) throw new Error(`Fetch ${url} → ${resp.status}`);
  const blob = await resp.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

function downloadBlobUrl(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function BragRow({ board, onShare, busy }) {
  const url = api.fileUrl(board.brag_board_path);
  const week = formatWeekLabel(board.week_start, board.week_end);
  const winner = board.winner_name ? board.winner_name.toUpperCase() : "NO CLEAR VICTOR";
  const isBusyThis = busy === board.id;

  return (
    <div
      className="px-4 py-3 flex flex-col gap-3"
      data-testid={`brag-board-row-${board.week_start}`}
    >
      {/* Metadata header line */}
      <div className="flex items-center gap-3 font-mono uppercase">
        <div className="label-mono w-20 flex-shrink-0">{week}</div>
        <div className="flex-1 min-w-0 truncate">
          <span className="font-bold">{winner}</span>
          <span className="label-mono ml-2 text-[var(--stakes-muted)]">
            · {board.photo_count} PROOF{board.photo_count === 1 ? "" : "S"}
          </span>
        </div>
        <div
          className="font-anton text-xl leading-none flex-shrink-0"
          style={{ color: "var(--stakes-child)" }}
        >
          {board.winner_points ? `${board.winner_points} pts` : "—"}
        </div>
      </div>

      {/* The collage itself — borderless square, cyan hairline frame */}
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="block border border-[var(--stakes-border)] hover:border-[var(--neon-blue)] transition-colors"
        data-testid={`brag-board-image-${board.week_start}`}
      >
        <img
          src={url}
          alt={`Brag board for ${week}`}
          className="w-full h-auto block"
          loading="lazy"
        />
      </a>

      {/* Share row */}
      <div className="flex justify-end">
        <button
          onClick={() => onShare(board)}
          disabled={isBusyThis}
          className="label-mono px-3 py-1 border border-[var(--neon-blue)] text-[var(--neon-blue)] hover:bg-[var(--neon-blue)] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid={`brag-board-share-${board.week_start}`}
        >
          {isBusyThis ? "[ SHARING… ]" : "[ SHARE STATUS ]"}
        </button>
      </div>
    </div>
  );
}

export default function BragBoardPanel({ leagueId, user }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [triggering, setTriggering] = useState(false);

  const isAdmin = user?.account_type === "admin";

  const reload = useCallback(async () => {
    if (!leagueId) return;
    try {
      const r = await api.getBragBoards(leagueId, 12);
      setItems(r.items || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load brag boards");
    }
  }, [leagueId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleShare = useCallback(async (board) => {
    if (!board?.brag_board_path) return;
    setBusy(board.id);
    const fileName = `stakes-brag-board-${board.week_start}.jpg`;
    const url = api.fileUrl(board.brag_board_path);
    const shareText = `// STAKES · ${board.league_name || "FAMILY ACCOUNTABILITY"}\nVICTOR: ${(board.winner_name || "—").toUpperCase()} (${board.winner_points || 0} PTS)`;
    try {
      // Strategy 1: file share (preferred — opens WhatsApp/Instagram)
      if (navigator.share && navigator.canShare) {
        try {
          const file = await fetchAsFile(url, fileName);
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: "STAKES · BRAG BOARD",
              text: shareText,
            });
            return;
          }
        } catch (innerErr) {
          // user-cancelled or file-share unsupported — fall through
          if (innerErr?.name === "AbortError") return;
        }
      }
      // Strategy 2: URL share
      if (navigator.share) {
        try {
          await navigator.share({
            title: "STAKES · BRAG BOARD",
            text: shareText,
            url,
          });
          return;
        } catch (innerErr) {
          if (innerErr?.name === "AbortError") return;
        }
      }
      // Strategy 3: download fallback (desktop browsers without share API)
      downloadBlobUrl(url, fileName);
      toast.success("Brag board downloaded — drop it in your group chat");
    } catch (e) {
      console.error("[BragBoardPanel] share failed:", e);
      toast.error("Couldn't open share sheet — try long-pressing the image");
    } finally {
      setBusy(null);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!leagueId || triggering) return;
    setTriggering(true);
    try {
      const r = await api.adminTriggerBragBoard(leagueId);
      if (r?.brag_board?.brag_board_path) {
        toast.success("Brag board generated · raw photos preserved");
        await reload();
      } else {
        toast.error("Generation returned no path");
      }
    } catch (e) {
      // Surface the FULL HTTP error so we can tell the difference between
      // a 4xx with a server-side detail, a 5xx crash, and a true network/
      // CORS failure. Plain axios `e.message === "Network Error"` is the
      // generic fallback when the response never reached the browser.
      const status = e?.response?.status;
      const serverDetail = e?.response?.data?.detail;
      const axiosMsg = e?.message;
      const code = e?.code; // ERR_NETWORK, ECONNABORTED, ERR_BAD_REQUEST…
      console.error("[BragBoardPanel] force-build failed:", {
        status, code, serverDetail, message: axiosMsg, raw: e,
      });
      let label;
      if (serverDetail) {
        label = `${status} · ${String(serverDetail)}`;
      } else if (status) {
        label = `HTTP ${status}${axiosMsg ? ` · ${axiosMsg}` : ""}`;
      } else if (code === "ERR_NETWORK") {
        label = "NETWORK ERROR · CHECK CONNECTION OR ADMIN AUTH TOKEN";
      } else if (code === "ECONNABORTED") {
        label = "TIMEOUT · TRY AGAIN IN A FEW SECONDS";
      } else {
        label = axiosMsg || "Generation failed";
      }
      toast.error(String(label).toUpperCase());
    } finally {
      setTriggering(false);
    }
  }, [leagueId, reload, triggering]);

  return (
    <div
      className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)]"
      data-testid="brag-board-panel"
    >
      {/* Header — mirrors Hall of Fame */}
      <div className="p-4 md:p-5 border-b border-[var(--stakes-border)] flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="label-mono">WEEKLY COLLAGE</div>
          <h3 className="font-anton text-2xl uppercase leading-none mt-1 tracking-tight">
            BRAG BOARD
          </h3>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="label-mono">
            {items === null
              ? ""
              : items.length === 0
              ? "0 WEEKS"
              : `${items.length} WEEK${items.length === 1 ? "" : "S"}`}
          </div>
          {isAdmin && (
            <button
              onClick={handleGenerate}
              disabled={triggering}
              className="label-mono px-3 py-1 border border-[var(--stakes-border)] hover:border-[var(--neon-blue)] hover:text-[var(--neon-blue)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="brag-board-force-generate"
              title="Admin only — force-build the brag board for the current week. Raw photos are NOT purged."
            >
              {triggering ? "[ BUILDING… ]" : "[ FORCE BUILD ]"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          className="p-6 label-mono text-center"
          style={{ color: "var(--stakes-primary)" }}
          data-testid="brag-board-error"
        >
          {String(error).toUpperCase()}
        </div>
      )}
      {!error && items === null && (
        <div className="p-6 label-mono text-center" data-testid="brag-board-loading">
          LOADING…
        </div>
      )}
      {!error && items && items.length === 0 && (
        <div className="p-6 label-mono text-center" data-testid="brag-board-empty">
          NO BRAG BOARDS YET — POST PROOF PHOTOS THIS WEEK
          {isAdmin && " · THEN HIT [ FORCE BUILD ]"}
        </div>
      )}
      {!error && items && items.length > 0 && (
        <div className="divide-y divide-[var(--stakes-border)]">
          {items.map((b) => (
            <BragRow key={b.id} board={b} onShare={handleShare} busy={busy} />
          ))}
        </div>
      )}
    </div>
  );
}
